import * as io from "io-ts";
import * as Either from "fp-ts/Either";
import { BlockImport, BlockModel } from ".";
import { withFallback, withMessage } from "io-ts-types";
import t from "../../../../../i18n";
import { DiagramEncoding } from "../encoding";
import { BlockData } from "./data";
import { Evolutions, Thresholds, Viewport } from "diagnostics-domain/dashboard/type";
import { short } from "diagnostics-lib/io";
export const BlockDiagramKind = withMessage(io.strict({ kind: io.literal("DIAGRAM") }), () =>
  t("schema.block.kind"),
);

export const BlockDiagramData = io.type({
  data: BlockData,
});

// const BlockDiagramLayerPosition = io.array(io.number);
const BlockDiagramLayerPosition = io.array(io.number);

const BlockDiagramLayer = io.intersection([
  io.type({
    position: BlockDiagramLayerPosition,
    anchor: io.strict({
      x: io.keyof({ left: null, center: null, right: null }),
      y: io.keyof({ top: null, center: null, bottom: null }),
    }),
    anchorRelativePositions: withFallback(io.array(io.number), []),
    encodings: io.array(DiagramEncoding),
    evolutions: Evolutions,
    showLabel: io.boolean,
    showDescription: io.boolean,
    thresholds: withMessage(
      io.refinement(Thresholds, thresholds => {
        const hasAbsoluteNegativeValues = thresholds.some(
          t => t.absolute && t.value !== null && t.value < 0,
        );

        if (hasAbsoluteNegativeValues) return false;

        const ltes = thresholds
          .filter(t => t.comparison === "lte")
          .map(t => t.value)
          .filter((v): v is NonNullable<number> => v !== null);

        const gtes = thresholds
          .filter(t => t.comparison === "gte")
          .map(t => t.value)
          .filter((v): v is NonNullable<number> => v !== null);

        return gtes.every(gte => (ltes.length > 0 ? ltes.every(lte => lte < gte) : true));
      }),
      thresholds => {
        if (
          Either.isRight(Thresholds.decode(thresholds)) &&
          (thresholds as Thresholds).some(t => t.absolute && t.value !== null && t.value < 0)
        ) {
          return t("project.dashboard.block.diagram.threshold.error.absolute");
        }
        return t("project.dashboard.block.diagram.threshold.error.inconsistent");
      },
    ),
  }),
  io.partial({ name: io.union([short(io.string), io.null]) }),
]);

export const BlockDiagramConfig = io.type({
  config: io.intersection([
    io.type({
      url: io.string,
      layers: io.array(BlockDiagramLayer),
      show_role: io.boolean,
    }),
    io.partial({
      live: io.boolean,
      show_all: io.boolean,
      viewport: Viewport,
      aggregate: io.boolean,
    }),
  ]),
});

export const BlockDiagramModel = io.intersection([
  BlockModel,
  BlockDiagramKind,
  BlockDiagramData,
  BlockDiagramConfig,
]);

export const BlockDiagramImport = io.intersection([
  BlockImport,
  BlockDiagramKind,
  BlockDiagramData,
  BlockDiagramConfig,
]);

export type BlockDiagramModelT = io.TypeOf<typeof BlockDiagramModel>;
export type BlockDiagramImportT = io.TypeOf<typeof BlockDiagramImport>;
